import React, { useState } from "react";
import SectionBadge from "@/components/shared/Title";
import { useTranslation } from "react-i18next";

import { FiPlus, FiX } from "react-icons/fi";
import { FaQuestion } from "react-icons/fa6";

import MotionDiv from "@/components/animations/MotionDiv";
import { fadeUp, stagger } from "@/animations/variants";
import { motion, AnimatePresence } from "framer-motion";

import { useFaqs } from "@/hooks/useFaqs";

export default function FAQSection() {
  const { t, i18n } = useTranslation();
  const isAR = i18n.language?.toLowerCase().startsWith("ar");

  const badge = t("faq.title", "Frequently Asked Questions");
  const subtitle = t(
    "faq.subtitle",
    "Choose the plan that fits your team’s needs."
  );

  // Fetch FAQs from backend
  const { faqs: questions, loading, error } = useFaqs(isAR);

  const [openIndex, setOpenIndex] = useState(0);
  const [expandedAll, setExpandedAll] = useState(false);

  const toggleAll = () => {
    setExpandedAll(!expandedAll);
    setOpenIndex(-1);
  };

  return (
    <section id="faq" className="px-4 py-10 md:px-10">
      <div className="mx-auto max-w-6xl">
        {/* HEADER */}
        <MotionDiv variants={fadeUp} className="mb-10 text-center">
          <SectionBadge text={badge} icon={<FaQuestion />} />
          {subtitle && (
            <MotionDiv variants={fadeUp}>
              <p className="my-5 text-sm text-gray-600 md:text-base">
                {subtitle}
              </p>
            </MotionDiv>
          )}
        </MotionDiv>

        {/* Expand All */}
        <MotionDiv variants={fadeUp} className="mb-4 flex justify-end">
          <button
            onClick={toggleAll}
            className="cursor-pointer text-sm font-medium text-primary underline"
          >
            {isAR ? "توسيع الكل" : "Expand All"}
          </button>
        </MotionDiv>

        {/* Show loading/error */}
        {loading && (
          <p className="text-gray-500">
            {isAR ? "جاري تحميل الأسئلة..." : "Loading FAQs..."}
          </p>
        )}
        {error && (
          <p className="text-red-500">
            {isAR ? "حدث خطأ أثناء تحميل الأسئلة" : error}
          </p>
        )}

        {/* ACCORDION */}
        {!loading && !error && questions.length > 0 && (
          <MotionDiv
            variants={stagger}
            className="space-y-5"
            dir={isAR ? "rtl" : "ltr"}
          >
            {questions.map((item, i) => {
              const isOpen = expandedAll || openIndex === i;

              return (
                <MotionDiv key={item.id} variants={fadeUp}>
                  <div className="rounded-lg border border-gray-200 bg-white px-5 shadow-sm transition">
                    {/* QUESTION */}
                    <button
                      onClick={() => setOpenIndex(openIndex === i ? -1 : i)}
                      className="flex w-full items-center justify-between py-4 cursor-pointer"
                    >
                      <span className="text-dark text-sm font-semibold">
                        {item.question}
                      </span>
                      {/* ICON ANIMATION */}
                      <motion.span
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        className="text-dark"
                      >
                        {isOpen ? <FiX size={16} /> : <FiPlus size={16} />}
                      </motion.span>
                    </button>

                    {/* ANSWER */}
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <p className="pb-4 text-sm leading-6 text-gray-600">
                            {item.answer}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </MotionDiv>
              );
            })}
          </MotionDiv>
        )}
      </div>
    </section>
  );
}